import React, { useState, useEffect } from 'react';
import { Modal, Button, Text, Textarea, Group, Loader } from '@mantine/core';
import { DialogErrorAlert } from '../DialogErrorAlert';

export interface EntityDetailApprovalModalProps {
    opened: boolean;
    onClose: () => void;
    decision: 'Approved' | 'Denied';
    entityService: any;
    fullEntity: any;
    entity: any;
    onSuccess: () => void;
}

export const EntityDetailApprovalModal: React.FC<EntityDetailApprovalModalProps> = ({
    opened,
    onClose,
    decision,
    entityService,
    fullEntity,
    entity,
    onSuccess,
}) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (opened) {
            setReason('');
            setError(null);
        }
    }, [opened, decision]);

    const handleClose = () => {
        if (submitting) return;
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        const displayEntity = fullEntity || entity;
        const id = displayEntity?.Id ?? displayEntity?.id;
        const trimmedReason = reason.trim();
        if (!id) {
            setError('Missing RequestApproval id.');
            return;
        }
        if (!trimmedReason) {
            setError('Reason is required.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await entityService.callRequestApprovalDecision(String(id), decision, trimmedReason);
            onSuccess();
        } catch (err: any) {
            const msg = (err && (err.message || err.toString())) || 'Failed to submit decision.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={submitting ? () => {} : handleClose}
            title={decision === 'Denied' ? 'Deny request' : 'Approve request'}
            size="lg"
            closeOnClickOutside={!submitting}
            closeOnEscape={!submitting}
        >
            <Text size="sm" c="dimmed" mb="sm">
                {decision === 'Denied'
                    ? 'Add a reason for denying this request.'
                    : 'Add a reason for approving this request.'}
            </Text>
            <DialogErrorAlert error={error} onClose={() => setError(null)} />
            <Textarea
                label="Reason"
                placeholder="Type a reason…"
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                autosize
                minRows={3}
                disabled={submitting}
            />
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    leftSection={submitting ? <Loader size={16} color="white" /> : undefined}
                    color={decision === 'Denied' ? 'red' : undefined}
                >
                    {submitting ? 'Submitting…' : (decision === 'Denied' ? 'Deny' : 'Approve')}
                </Button>
            </Group>
        </Modal>
    );
};
